import { useCallback, useMemo, useState } from 'react'

import { warnDev } from '@/utils/warnDev'

import { Column } from '../TableBase.types'

export type ColumnVisibilityItem = {
  id: string
  label: string
  visible: boolean
}

export const useColumnVisibility = <Data, SortAttributes extends string>(
  columns: Column<Data, SortAttributes>[]
) => {
  const [columnVisibility, setColumnVisibility] = useState<Map<string, boolean>>(
    new Map(columns.map((column) => [column.id, column?.hiddenByDefault ?? true]))
  )

  const checkColumnVisibility = useCallback(
    (column: string | Column<Data, SortAttributes>) => {
      const columnId = typeof column === 'string' ? column : column.id
      const col = columnVisibility.get(columnId)

      if (col === undefined) {
        warnDev(`Column with id ${columnId} does not exist. (Column visibility check)`)
        return false
      }

      return col
    },
    [columnVisibility]
  )

  const orderedColumnVisibility: ColumnVisibilityItem[] = useMemo(() => {
    return columns.map((column) => ({
      id: column.id,
      label: column.label,
      visible: columnVisibility.get(column.id) ?? true
    }))
  }, [columns, columnVisibility])

  const toggleColumnVisibility = useCallback(
    (column: string | Column<Data, SortAttributes>) => {
      setColumnVisibility((prev) => {
        const columnId = typeof column === 'string' ? column : column.id
        const isCurrentlyVisible = prev.get(columnId)

        if (isCurrentlyVisible === undefined) {
          warnDev(`Column with id ${columnId} does not exist. (Column visibility toggle)`)
          return prev
        }

        // If trying to hide a column, check if it's the last visible one and if so - make all columns visible instead
        if (isCurrentlyVisible) {
          const visibleColumnCount = Array.from(prev.values()).filter(Boolean).length

          if (visibleColumnCount <= 1) {
            const newVisibility = new Map(prev)
            columns.forEach((col) => {
              newVisibility.set(col.id, true)
            })
            return newVisibility
          }
        }

        return new Map(prev).set(columnId, !isCurrentlyVisible)
      })
    },
    [columns]
  )

  return {
    columnVisibility: orderedColumnVisibility,
    toggleColumnVisibility,
    checkColumnVisibility
  }
}
